import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft, ScrollText, Calendar, Gavel, FileText, Edit2, Ban, Trash2, AlertCircle,
  Clock, History,
} from "lucide-react";
import { toast } from "sonner";
import AppShell, { getActiveSchemeId } from "@/components/AppShell";
import ConfirmationGate, { useConfirmationGate } from "@/components/ConfirmationGate";
import { useAuth } from "@/auth/AuthContext";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";

const CATEGORIES = [
  { value: "PETS", label: "Pets" },
  { value: "PARKING", label: "Parking" },
  { value: "SHORT_STAY", label: "Short-stay" },
  { value: "COMMON_PROPERTY", label: "Common property" },
  { value: "NOISE", label: "Noise" },
  { value: "OTHER", label: "Other" },
];

const STATUS_STYLE = {
  ACTIVE: { bg: "#dcfce7", color: "#166534" },
  AMENDED: { bg: "var(--blue-light)", color: "var(--blue-dark)" },
  PROPOSED: { bg: "#fef3c7", color: "#92400e" },
  REPEALED: { bg: "#fee2e2", color: "#991b1b" },
};

function StatusPill({ status, large }) {
  const s = STATUS_STYLE[status] || STATUS_STYLE.ACTIVE;
  return (
    <span
      className={`inline-flex items-center font-semibold uppercase tracking-wider rounded-md ${large ? "px-2.5 py-1 text-xs" : "px-2 py-0.5 text-[11px]"}`}
      style={{ background: s.bg, color: s.color }}
      data-testid="bylaw-detail-status-pill"
    >
      {status}
    </span>
  );
}

function HistoryRow({ entry }) {
  const [expanded, setExpanded] = useState(false);
  const labels = {
    CREATED: { color: "var(--blue-dark)", bg: "var(--blue-light)", label: "Created" },
    AMENDED: { color: "#92400e", bg: "#fef3c7", label: "Amended" },
    REPEALED: { color: "#991b1b", bg: "#fee2e2", label: "Repealed" },
  };
  const l = labels[entry.change_type] || labels.AMENDED;
  return (
    <li className="py-3 first:pt-0" data-testid={`bylaw-history-${entry.history_id}`}>
      <div className="flex items-center gap-2 flex-wrap">
        <span
          className="inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-semibold uppercase tracking-wider"
          style={{ background: l.bg, color: l.color }}
        >
          {l.label}
        </span>
        <span className="text-xs" style={{ color: "var(--slate)" }}>
          {new Date(entry.changed_at).toLocaleString()}
        </span>
        {entry.notes && (
          <span className="text-xs italic truncate" style={{ color: "var(--slate)" }}>
            &mdash; {entry.notes}
          </span>
        )}
      </div>
      {entry.prior_text && (
        <button
          onClick={() => setExpanded(!expanded)}
          className="mt-2 text-xs underline"
          style={{ color: "var(--blue-dark)" }}
          data-testid={`bylaw-history-toggle-${entry.history_id}`}
        >
          {expanded ? "Hide prior text" : "See prior text"}
        </button>
      )}
      {expanded && entry.prior_text && (
        <div
          className="mt-2 p-3 rounded-md text-sm whitespace-pre-wrap"
          style={{ background: "#f1f5f9", color: "var(--navy)", borderLeft: "3px solid var(--slate)" }}
        >
          {entry.prior_text}
        </div>
      )}
    </li>
  );
}

export default function BylawDetail() {
  const { bylaw_id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const schemeId = getActiveSchemeId();
  const [bl, setBl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [members, setMembers] = useState([]);
  const [editOpen, setEditOpen] = useState(false);
  const [repealOpen, setRepealOpen] = useState(false);
  const [repealNotes, setRepealNotes] = useState("");
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [editForm, setEditForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const repealGate = useConfirmationGate({
    onConfirmed: async () => { toast.success("By-law repealed"); setRepealOpen(false); setRepealNotes(""); await load(); },
  });

  const load = async () => {
    setLoading(true);
    try {
      const [{ data: m }, { data: bldata }] = await Promise.all([
        api.get(`/schemes/${schemeId}/members`),
        api.get(`/schemes/${schemeId}/bylaws/${bylaw_id}`),
      ]);
      setMembers(m);
      setBl(bldata);
    } catch (e) {
      setError(apiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!schemeId) {
      navigate("/scheme/bylaws");
      return;
    }
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bylaw_id]);

  const myMembership = members.find((mm) => mm.user_id === user?.user_id);
  const myRole = myMembership?.role;
  const isChair = myRole === "chair" || user?.system_role === "platform_admin";
  const isCommittee = myRole === "committee";

  const openEdit = () => {
    setEditForm({
      by_law_number: bl.by_law_number,
      title: bl.title,
      full_text: bl.full_text,
      category: bl.category,
      status: bl.status,
      effective_date: bl.effective_date ? String(bl.effective_date).slice(0, 10) : "",
      notes: "",
    });
    setEditOpen(true);
  };

  const submitEdit = async () => {
    setSaving(true);
    try {
      const body = { ...editForm };
      if (!body.effective_date) delete body.effective_date;
      if (!body.notes) delete body.notes;
      await api.patch(`/schemes/${schemeId}/bylaws/${bylaw_id}`, body);
      toast.success("By-law updated");
      setEditOpen(false);
      await load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  const submitRepeal = async () => {
    setSaving(true);
    try {
      // Optional reason is passed as body.notes → backend writes it into
      // bylaw_history.notes (bylaws.py:363). Kept optional so the gate still
      // opens on empty input; hash is stable across the fire → confirm round-trip
      // because compute_calculation_hash canonicalises the whole body minus
      // `confirmation`.
      const body = repealNotes.trim() ? { notes: repealNotes.trim() } : {};
      const result = await repealGate.fire(
        "post",
        `/schemes/${schemeId}/bylaws/${bylaw_id}/repeal`,
        body,
        { __scheme_id: schemeId },
      );
      if (result) {
        toast.success("By-law repealed");
        setRepealOpen(false);
        setRepealNotes("");
        await load();
      }
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  const submitDelete = async () => {
    setSaving(true);
    try {
      await api.delete(`/schemes/${schemeId}/bylaws/${bylaw_id}`);
      toast.success("By-law deleted");
      navigate("/scheme/bylaws");
    } catch (e) {
      toast.error(apiError(e));
      setSaving(false);
    }
  };

  return (
    <AppShell>
      <div className="max-w-4xl mx-auto px-5 lg:px-8 py-8" data-testid="bylaw-detail-page">
        <Link to="/scheme/bylaws">
          <Button variant="ghost" size="sm" className="h-8 mb-4 -ml-2" data-testid="bylaw-detail-back">
            <ArrowLeft size={14} className="mr-1.5" />
            Back to by-laws
          </Button>
        </Link>

        {loading ? (
          <div className="space-y-4">
            <Skeleton className="h-10 w-2/3" />
            <Skeleton className="h-24 w-full" />
            <Skeleton className="h-48 w-full" />
          </div>
        ) : error ? (
          <div
            className="rounded-lg p-4 text-sm"
            style={{ background: "var(--alert-light)", color: "var(--alert)" }}
            data-testid="bylaw-detail-error"
          >
            <AlertCircle size={16} className="inline mr-2" />
            {error}
          </div>
        ) : bl ? (
          <>
            {/* Header */}
            <div className="mb-6">
              <div className="flex items-center gap-2 flex-wrap mb-2">
                <span
                  className="text-sm font-mono font-semibold px-2 py-1 rounded"
                  style={{ background: "#f1f5f9", color: "var(--navy)" }}
                  data-testid="bylaw-detail-number"
                >
                  By-law #{bl.by_law_number}
                </span>
                <StatusPill status={bl.status} large />
                <span
                  className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium"
                  style={{ background: "#f1f5f9", color: "var(--slate)" }}
                  data-testid="bylaw-detail-category"
                >
                  {CATEGORIES.find((c) => c.value === bl.category)?.label || bl.category}
                </span>
              </div>
              <h1
                className="text-2xl font-bold tracking-tight"
                style={{ color: "var(--navy)" }}
                data-testid="bylaw-detail-title"
              >
                {bl.title}
              </h1>
            </div>

            {/* Dates */}
            <div
              className="rounded-xl bg-white border p-4 mb-5 flex flex-wrap gap-x-6 gap-y-2 text-sm"
              style={{ borderColor: "var(--border)" }}
              data-testid="bylaw-detail-dates"
            >
              {bl.effective_date && (
                <div>
                  <span className="inline-flex items-center gap-1 text-[11px] uppercase tracking-wider font-semibold" style={{ color: "var(--slate)" }}>
                    <Calendar size={11} />
                    Effective
                  </span>
                  <div className="font-medium" style={{ color: "var(--navy)" }}>
                    {String(bl.effective_date).slice(0, 10)}
                  </div>
                </div>
              )}
              {bl.amended_date && (
                <div>
                  <span className="inline-flex items-center gap-1 text-[11px] uppercase tracking-wider font-semibold" style={{ color: "var(--slate)" }}>
                    <Clock size={11} />
                    Amended
                  </span>
                  <div className="font-medium" style={{ color: "var(--navy)" }}>
                    {String(bl.amended_date).slice(0, 10)}
                  </div>
                </div>
              )}
              {bl.repealed_date && (
                <div>
                  <span className="inline-flex items-center gap-1 text-[11px] uppercase tracking-wider font-semibold" style={{ color: "var(--slate)" }}>
                    <Ban size={11} />
                    Repealed
                  </span>
                  <div className="font-medium" style={{ color: "var(--navy)" }}>
                    {String(bl.repealed_date).slice(0, 10)}
                  </div>
                </div>
              )}
              {!bl.effective_date && !bl.amended_date && !bl.repealed_date && (
                <span className="text-xs" style={{ color: "var(--slate)" }}>No dates recorded yet.</span>
              )}
            </div>

            {/* Linked refs */}
            {(bl.linked_meeting || bl.linked_document) && (
              <div className="flex flex-wrap gap-2 mb-5" data-testid="bylaw-detail-links">
                {bl.linked_meeting && (
                  <Link
                    to={`/scheme/meetings/${bl.linked_meeting.meeting_id}`}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium border hover:bg-slate-50 transition-colors"
                    style={{ borderColor: "var(--border)", color: "var(--navy)" }}
                    data-testid="bylaw-linked-meeting"
                  >
                    <Gavel size={12} style={{ color: "var(--blue)" }} />
                    Linked to {bl.linked_meeting.meeting_type}: {bl.linked_meeting.title}
                  </Link>
                )}
                {bl.linked_document && (
                  <span
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium border"
                    style={{ borderColor: "var(--border)", color: "var(--navy)" }}
                    data-testid="bylaw-linked-document"
                  >
                    <FileText size={12} style={{ color: "var(--blue)" }} />
                    {bl.linked_document.filename}
                  </span>
                )}
              </div>
            )}

            {/* Full text */}
            <div
              className="rounded-xl bg-white border p-5 mb-5"
              style={{ borderColor: "var(--border)" }}
              data-testid="bylaw-detail-text"
            >
              <div className="flex items-center gap-2 mb-3">
                <ScrollText size={16} style={{ color: "var(--blue)" }} />
                <span className="text-xs font-semibold uppercase tracking-wider" style={{ color: "var(--slate)" }}>
                  Full text
                </span>
              </div>
              <div
                className="text-sm whitespace-pre-wrap leading-relaxed"
                style={{ color: "var(--navy)" }}
              >
                {bl.full_text}
              </div>
            </div>

            {/* Chair/committee CTAs */}
            {(isChair || isCommittee) && (
              <div className="flex flex-wrap gap-2 mb-6" data-testid="bylaw-detail-actions">
                {(isChair || isCommittee) && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={openEdit}
                    data-testid="bylaw-edit-button"
                  >
                    <Edit2 size={13} className="mr-1.5" />
                    Edit
                  </Button>
                )}
                {isChair && bl.status !== "REPEALED" && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setRepealOpen(true)}
                    data-testid="bylaw-repeal-button"
                    style={{ color: "#991b1b", borderColor: "#fecaca" }}
                  >
                    <Ban size={13} className="mr-1.5" />
                    Repeal
                  </Button>
                )}
                {isChair && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setDeleteOpen(true)}
                    data-testid="bylaw-delete-button"
                    style={{ color: "#991b1b" }}
                  >
                    <Trash2 size={13} className="mr-1.5" />
                    Delete
                  </Button>
                )}
              </div>
            )}

            {/* History timeline — chair/committee only */}
            {bl.history && bl.history.length > 0 && (
              <div
                className="rounded-xl bg-white border p-5"
                style={{ borderColor: "var(--border)" }}
                data-testid="bylaw-detail-history"
              >
                <div className="flex items-center gap-2 mb-3">
                  <History size={16} style={{ color: "var(--blue)" }} />
                  <h2 className="text-sm font-semibold" style={{ color: "var(--navy)" }}>
                    Change history
                  </h2>
                  <span className="text-xs" style={{ color: "var(--slate)" }}>
                    ({bl.history.length} {bl.history.length === 1 ? "entry" : "entries"})
                  </span>
                </div>
                <ul className="divide-y" style={{ borderColor: "var(--border)" }}>
                  {bl.history.map((h) => <HistoryRow key={h.history_id} entry={h} />)}
                </ul>
              </div>
            )}

            {/* Edit modal */}
            <Dialog open={editOpen} onOpenChange={setEditOpen}>
              <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="bylaw-edit-modal">
                <DialogHeader>
                  <DialogTitle>Edit by-law #{bl.by_law_number}</DialogTitle>
                  <DialogDescription>
                    Changing the full text creates an &ldquo;Amended&rdquo; history entry and flips the status if currently Active.
                  </DialogDescription>
                </DialogHeader>
                {editForm && (
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="text-xs font-semibold mb-1.5 block">By-law number</Label>
                        <Input
                          value={editForm.by_law_number}
                          onChange={(e) => setEditForm({ ...editForm, by_law_number: e.target.value })}
                          data-testid="bylaw-edit-number"
                        />
                      </div>
                      <div>
                        <Label className="text-xs font-semibold mb-1.5 block">Category</Label>
                        <Select
                          value={editForm.category}
                          onValueChange={(v) => setEditForm({ ...editForm, category: v })}
                        >
                          <SelectTrigger data-testid="bylaw-edit-category"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {CATEGORIES.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div>
                      <Label className="text-xs font-semibold mb-1.5 block">Title</Label>
                      <Input
                        value={editForm.title}
                        onChange={(e) => setEditForm({ ...editForm, title: e.target.value })}
                        data-testid="bylaw-edit-title"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="text-xs font-semibold mb-1.5 block">Status</Label>
                        <Select
                          value={editForm.status}
                          onValueChange={(v) => setEditForm({ ...editForm, status: v })}
                        >
                          <SelectTrigger data-testid="bylaw-edit-status"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="PROPOSED">Proposed</SelectItem>
                            <SelectItem value="ACTIVE">Active</SelectItem>
                            <SelectItem value="AMENDED">Amended</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-xs font-semibold mb-1.5 block">Effective date</Label>
                        <Input
                          type="date"
                          value={editForm.effective_date}
                          onChange={(e) => setEditForm({ ...editForm, effective_date: e.target.value })}
                          data-testid="bylaw-edit-effective-date"
                        />
                      </div>
                    </div>
                    <div>
                      <Label className="text-xs font-semibold mb-1.5 block">Full text (markdown)</Label>
                      <Textarea
                        rows={8}
                        value={editForm.full_text}
                        onChange={(e) => setEditForm({ ...editForm, full_text: e.target.value })}
                        data-testid="bylaw-edit-text"
                      />
                    </div>
                    <div>
                      <Label className="text-xs font-semibold mb-1.5 block">
                        Amendment note <span className="font-normal text-slate-500">(optional, only for text changes)</span>
                      </Label>
                      <Input
                        value={editForm.notes}
                        onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })}
                        placeholder="e.g. Updated to reflect 2026 AGM resolution"
                        data-testid="bylaw-edit-notes"
                      />
                    </div>
                  </div>
                )}
                <DialogFooter>
                  <Button variant="ghost" onClick={() => setEditOpen(false)} data-testid="bylaw-edit-cancel">
                    Cancel
                  </Button>
                  <Button
                    onClick={submitEdit}
                    disabled={saving}
                    style={{ background: "var(--blue)", color: "white" }}
                    data-testid="bylaw-edit-save"
                  >
                    {saving ? "Saving\u2026" : "Save"}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>

            {/* Repeal confirm */}
            <Dialog open={repealOpen} onOpenChange={(o) => { setRepealOpen(o); if (!o) setRepealNotes(""); }}>
              <DialogContent data-testid="bylaw-repeal-modal">
                <DialogHeader>
                  <DialogTitle>Repeal by-law #{bl.by_law_number}?</DialogTitle>
                  <DialogDescription>
                    The by-law will be marked REPEALED, hidden from owners, and a history entry recorded.
                    This cannot be undone (but the text is preserved in history).
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-1.5 pt-1">
                  <label htmlFor="bylaw-repeal-reason" className="text-xs font-medium" style={{ color: "var(--slate)" }}>
                    Reason (optional) — recorded to by-law history
                  </label>
                  <Textarea
                    id="bylaw-repeal-reason"
                    value={repealNotes}
                    onChange={(e) => setRepealNotes(e.target.value)}
                    placeholder="e.g. Superseded by by-law 12 passed at 2026 AGM"
                    rows={3}
                    className="text-sm"
                    data-testid="bylaw-repeal-reason"
                  />
                </div>
                <DialogFooter>
                  <Button variant="ghost" onClick={() => { setRepealOpen(false); setRepealNotes(""); }} data-testid="bylaw-repeal-cancel">
                    Cancel
                  </Button>
                  <Button
                    onClick={submitRepeal}
                    disabled={saving}
                    style={{ background: "#991b1b", color: "white" }}
                    data-testid="bylaw-repeal-confirm"
                  >
                    {saving ? "Repealing\u2026" : "Repeal"}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>

            {/* Delete confirm */}
            <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
              <DialogContent data-testid="bylaw-delete-modal">
                <DialogHeader>
                  <DialogTitle>Delete by-law #{bl.by_law_number}?</DialogTitle>
                  <DialogDescription>
                    Soft-deletes the by-law and hides it from all views. Use Repeal for proper records;
                    Delete is for accidental drafts only.
                  </DialogDescription>
                </DialogHeader>
                <DialogFooter>
                  <Button variant="ghost" onClick={() => setDeleteOpen(false)} data-testid="bylaw-delete-cancel">
                    Cancel
                  </Button>
                  <Button
                    onClick={submitDelete}
                    disabled={saving}
                    style={{ background: "#991b1b", color: "white" }}
                    data-testid="bylaw-delete-confirm"
                  >
                    {saving ? "Deleting\u2026" : "Delete"}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
            {/* Confirmation gate for BYLAW_REPEAL — opens on top of the repeal
                dialog once the backend responds with 400 confirmation_required. */}
            <ConfirmationGate {...repealGate.gateProps} />
          </>
        ) : null}
      </div>
    </AppShell>
  );
}
